import { Box, Button, type ButtonProps, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from "react";

import { AsyncButtonStartIconValues, renderAsyncButtonIcon } from "./async-button-icon";

export interface AsyncButtonProps extends Omit<ButtonProps, "onClick" | "startIcon" | "endIcon" | "children"> {
  text?: ReactNode;
  children?: ReactNode;
  iconButton?: boolean;
  startIconValue?: AsyncButtonStartIconValues | null;
  endIconValue?: AsyncButtonStartIconValues | null;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  tooltip?: ReactNode;
  isDisabled?: boolean;
  sync?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => unknown;
}

export function AsyncButton(props: AsyncButtonProps): React.JSX.Element {
  const {
    text,
    children,
    iconButton = false,
    startIconValue,
    endIconValue,
    startIcon,
    endIcon,
    tooltip,
    isDisabled = false,
    sync = false,
    onClick,
    disabled = false,
    color,
    variant = "outlined",
    size = "medium",
    sx,
    ...buttonProps
  } = props;

  const [isPending, setIsPending] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return (): void => {
      isMountedRef.current = false;
    };
  }, []);

  const handleClick = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
    if (onClick === undefined) {
      return;
    }

    const clickResult = onClick(event);

    if (sync || !(clickResult instanceof Promise)) {
      return;
    }

    setIsPending(true);

    try {
      await clickResult;
    } finally {
      if (isMountedRef.current) {
        setIsPending(false);
      }
    }
  };

  const isRealDisabled = disabled || isDisabled || isPending;
  const buttonColor = color ?? (startIconValue === AsyncButtonStartIconValues.DeleteIconValue ? "error" : "primary");
  const content = children ?? text;
  const accessibleLabel = typeof content === "string" ? content : typeof tooltip === "string" ? tooltip : undefined;

  const buttonControl = iconButton ? (
    <IconButton
      color={buttonColor}
      size={size}
      sx={sx}
      disabled={isRealDisabled}
      aria-label={accessibleLabel}
      onClick={(event) => void handleClick(event)}
    >
      {isPending ? <CircularProgress size={20} /> : (renderAsyncButtonIcon(startIconValue) ?? startIcon ?? content)}
    </IconButton>
  ) : (
    <Button
      color={buttonColor}
      size={size}
      variant={variant}
      sx={sx}
      disabled={isRealDisabled}
      startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : (renderAsyncButtonIcon(startIconValue) ?? startIcon)}
      endIcon={renderAsyncButtonIcon(endIconValue) ?? endIcon}
      onClick={(event) => void handleClick(event)}
      {...buttonProps}
    >
      {content}
    </Button>
  );

  if (tooltip === undefined || tooltip === null) {
    return buttonControl;
  }

  return (
    <Tooltip title={tooltip}>
      <Box component="span" sx={{ display: "inline-flex" }}>
        {buttonControl}
      </Box>
    </Tooltip>
  );
}
